import { useQuery } from "@tanstack/react-query";
import { hub } from "@/lib/hub-client";

export type JobStatus =
  | "pending"
  | "holding"
  | "running"
  | "completed"
  | "failed"
  | "refunded"
  | "cancelled";

export interface JobSummary {
  job_id: string;
  outcome_id: string;
  partner_id: string;
  price_tokens: number;
  captured_amount: number | null;
  refunded_amount: number;
  status: JobStatus;
  error_code: string | null;
  error_message: string | null;
  deadline_at: number;
  created_at: string;
  updated_at: string;
}

export interface JobDetail extends JobSummary {
  user_id: string;
  output: Record<string, unknown> | null;
}

export interface JobList {
  items: JobSummary[];
  next_cursor: string | null;
}

export function useJobs(status?: JobStatus) {
  return useQuery({
    queryKey: ["jobs", status ?? "all"],
    queryFn: async () => {
      const params: Record<string, string> = { limit: "20" };
      if (status) params.status = status;
      const res = await hub.GET<JobList>("/v1/jobs", { params });
      if (!res.data) throw new Error("jobs fetch failed");
      return res.data;
    },
  });
}

export function useJob(jobId: string) {
  return useQuery({
    queryKey: ["job", jobId],
    queryFn: async () => {
      const res = await hub.GET<JobDetail>(`/v1/jobs/${jobId}`);
      if (res.response.status === 404) return null;
      if (!res.data) throw new Error("job fetch failed");
      return res.data;
    },
    enabled: Boolean(jobId),
    refetchInterval: (q) => {
      const status = (q.state.data as JobDetail | null)?.status;
      if (status === "running" || status === "holding" || status === "pending") {
        return 2_000;
      }
      return false;
    },
  });
}
